import {
  ChangeEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Container, ErrorMessage } from "./messageAttachment.styles";
import { Spinner, useSnackbar } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { useTranslation } from "react-i18next";
import { UploadResponse, useUploadAttachment } from "./hooks";
import { isAxiosError } from "axios";
import { Stack } from "@mui/material";
import { FileUploader } from "./FileUploader";
import { FilePreview } from "./FilePreview/FilePreview";
import {
  supportedFileExtensions,
  supportedFileExtensionsSchema,
} from "@mindlercare/schema";

const getFileExtension = (filename?: string): string | undefined => {
  if (!filename) return undefined;
  const match = /\.([^.]+)$/.exec(filename);
  return match ? match[1] : undefined;
};

type Props = {
  patientUserId: number;
  onAttachmentUpload: (attachmentKeys: string[]) => void;
};

// The actual limit is 6MB because of Lambda limits.
// When testing, the limit seems to be closer to 5MB.
// However we want some buffer to not experience issues with the upload.
// This number is tried and tested a lot and seems to work well. We communicate 4MB to the user
// so there is some buffer also upwards.
const MAX_FILE_SIZE_MB = 4.45;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const MessageAttachment = ({
  patientUserId,
  onAttachmentUpload,
}: PropsWithChildren<Props>) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState<UploadResponse[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync, isPending: isLoading } = useUploadAttachment();

  const uploadFile = useCallback(
    async (f: File) => {
      const formData = new FormData();
      formData.append("attachment", f);

      try {
        const res = await mutateAsync({
          formData,
          patientUserId: patientUserId,
        });

        return res.data;
      } catch (err) {
        if (isAxiosError(err)) {
          setError(err.response?.data.message);
        } else {
          setError(t("MESSAGE_ATTACHMENT_UPLOAD_ERROR"));
        }
      }
    },
    [mutateAsync, patientUserId, t],
  );

  const handleFile = useCallback(
    async (file: File) => {
      if (!file) {
        setError(t("MESSAGE_ATTACHMENT_NO_FILE_SELECTED"));
        return;
      }
      if (!getFileExtension(file.name)) {
        setError(t("MESSAGE_ATTACHMENT_NO_FILE_EXTENSION"));
        return;
      }
      if (
        !supportedFileExtensionsSchema.safeParse(getFileExtension(file.name))
          .success
      ) {
        setError(
          t("MESSAGE_ATTACHMENT_NOT_SUPPORTED", {
            fileFormats: supportedFileExtensions
              .map((ext) => `.${ext}`)
              .join(", "),
          }),
        );
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(t("MESSAGE_ATTACHMENT_TOO_BIG_FILE"));
        return;
      }
      if (files.find((f) => f.originalFileName === file.name)) {
        enqueueSnackbar({
          text: t("MESSAGE_ATTACHMENT_DUPLICATE"),
          isError: true,
        });
        return;
      }
      setError(null);
      const uploadedFile = await uploadFile(file);
      if (!uploadedFile) return;
      setFiles((files) => [...files, uploadedFile]);
    },
    [enqueueSnackbar, files, t, uploadFile],
  );

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        setError(t("MESSAGE_ATTACHMENT_NO_FILE_SELECTED"));
        return;
      }
      const file = Array.from(event.target.files);

      for (const f of file) {
        await handleFile(f);
      }
    },
    [handleFile, t],
  );

  const handleDelete = useCallback((fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.fileName !== fileName));
  }, []);

  useEffect(() => {
    onAttachmentUpload(files.map((f) => f.fileName));
  }, [files, onAttachmentUpload]);

  return (
    <div>
      <Typography
        sx={{ marginBottom: "6px" }}
        variant="body-x-small"
        color={"GRAY_BLACK"}
      >
        {t("MESSAGE_ATTACHMENT_TITLE")}
      </Typography>
      <Container>
        <FileUploader onChange={handleChange} />
        {files.length > 0 && (
          <Stack gap={1}>
            {files.map((file) => (
              <FilePreview
                type="create"
                key={file.fileName}
                attachmentKey={file.fileName}
                onDelete={handleDelete}
              />
            ))}
          </Stack>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {isLoading && <Spinner />}
      </Container>
    </div>
  );
};
